import { Loader } from 'components';
import { useInfoPopup } from 'hooks';
import debounce from 'lodash/debounce';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import { useMedia } from 'react-use';
import { doUpdateCommunityNotificationsSettings } from 'store/actionCreators/currentCommunity';
import {
  getCurrentCommunity,
  getIsMenuOpen,
  getUpdateCurrentCommunityLoading,
} from 'store/selectors/currentCommunity';
import { breakpoints } from 'theme';
import { CommunityNotificationClass, CommunityNotificationValues } from 'types';

import Card from './Card';
import config from './config';
import {
  ContentWrapper,
  StyledCardsWrapper,
  StyledCardWrapper,
  StyledClassBox,
  StyledFloatingLoader,
  StyledHeader,
  StyledRow,
  StyledTypeBox,
} from './styled';
import { NotificationType, NotificationTypeIcon } from './types';

const NotificationsPage = () => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const { showInfoPopup } = useInfoPopup();
  const dispatch = useDispatch();
  const community = useSelector(getCurrentCommunity);
  const isMenuOpen = useSelector(getIsMenuOpen);
  const isUpdating = useSelector(getUpdateCurrentCommunityLoading);
  const isDownMdPlus = useMedia(breakpoints.downMdPlus);
  const isDownLg = useMedia(breakpoints.downLg);
  const eventTypeIcon = Object.values(NotificationTypeIcon);
  const onSuccess = debounce(() => {
    showInfoPopup({
      type: 'success',
      title: t('common.editSuccess.title'),
      message: t('communitySettings.editSuccess'),
      duration: 3000,
      noButton: true,
      loop: false,
    });
  }, 1000);

  const onSubmit = (
    data: CommunityNotificationValues,
    notificationsType: NotificationType,
    notificationsClass: CommunityNotificationClass,
  ) => {
    const typeConfig = config[notificationsType];
    const values: CommunityNotificationValues = { enabled: data.enabled };

    if (typeConfig.updatedFieldsFilter) {
      values.fields = data.fields;
    }
    if (typeConfig.eventRepliesFilter) {
      values.replies = data.replies.filter(
        (r) => r !== 'penalty' && r !== 'attended' && r !== 'awaiting',
      );
      values.penalty = data.replies.some((r) => r === 'penalty');
      values.attended = data.replies.some((r) => r === 'attended');
      values.awaiting = data.replies.some((r) => r === 'awaiting');
    }
    if (typeConfig.timeInterval) {
      values.rates = data.rates;
    }

    const updatedNotifications = {
      ...community.notifications,
    };

    updatedNotifications.events[notificationsType][notificationsClass] = values;

    dispatch(
      doUpdateCommunityNotificationsSettings({
        data: {
          id: community.id,
          category: community.category,
          notifications: updatedNotifications,
        },
        preventUpdateOnSuccess: true,
        onSuccess,
      }),
    );
  };

  return community.eventResponses ? (
    <ContentWrapper>
      {isUpdating && (
        <StyledFloatingLoader>
          <Loader size="60px" type={null} />
        </StyledFloatingLoader>
      )}
      {!isDownMdPlus && (
        <StyledHeader>
          {!isDownLg && <StyledTypeBox></StyledTypeBox>}
          <StyledClassBox isMenuOpen={isMenuOpen}>
            <span>{t('communitySettings.notificationsClasses.0')}</span>
          </StyledClassBox>
          <StyledClassBox isMenuOpen={isMenuOpen}>
            <span>{t('communitySettings.notificationsClasses.1')}</span>
          </StyledClassBox>
          <StyledClassBox isMenuOpen={isMenuOpen}>
            <span>{t('communitySettings.notificationsClasses.2')}</span>
          </StyledClassBox>
        </StyledHeader>
      )}
      {Object.keys(config).map((key: NotificationType, ind) => (
        <StyledRow key={key}>
          <StyledTypeBox>
            <span>{eventTypeIcon[ind]}</span>
            <span>{t(`communitySettings.notificationsTypes.${ind}`)}</span>
          </StyledTypeBox>
          <StyledCardsWrapper>
            {Object.keys(CommunityNotificationClass).map(
              (notificationClass, index) => (
                <Fragment key={notificationClass}>
                  {isDownMdPlus && (
                    <StyledClassBox isMenuOpen={isMenuOpen}>
                      <span>
                        {t(`communitySettings.notificationsClasses.${index}`)}
                      </span>
                    </StyledClassBox>
                  )}
                  <StyledCardWrapper>
                    <Card
                      notificationsType={key}
                      notificationsClass={
                        CommunityNotificationClass[notificationClass]
                      }
                      config={config[key]}
                      data={
                        community.notifications.events[key][
                          CommunityNotificationClass[notificationClass]
                        ]
                      }
                      eventResponses={community.eventResponses}
                      onSubmit={onSubmit}
                    />
                  </StyledCardWrapper>
                </Fragment>
              ),
            )}
          </StyledCardsWrapper>
        </StyledRow>
      ))}
    </ContentWrapper>
  ) : (
    <Loader />
  );
};

export default NotificationsPage;
